/** @module csi/widget/_svg/CompositeTransform */
define([
	"csi/_base/underscore",
	"csi/dojoExtensions",
	"csi/html",
	"csi/locale",
	"csi/view/Toolbar",
	"./_Plugin",
	"./PluginFactory",
	"./util"
], function(_, dojoExtensions, html, locale, Toolbar, _Plugin, PluginFactory, util) {

	var PLUGINS = [
		{
			title: ["CanvasShapeAlignDistribute"],
			pluginGroups: [{
					plugins: ["shapeAlignLeft","shapeAlignHCenter","shapeAlignRight","shapeAlignTop","shapeAlignVCenter","shapeAlignBottom"]
				},{
					plugins: ["shapeDistributeHorizontal","shapeDistributeVertical"]
				}
			]
		},{
			title: ["CanvasTransformArrange"],
			plugins: ["front","back","flipVertical","flipHorizontal","rotate90CW","rotate90CCW","points"]
		},{
			title: ["CanvasImageTools"],
			plugins: ["CanvasScaleImage", "CropPoints", "CanvasCropImage", "addImageToPlaceholder", "ImageReset"]
		},{
			title: ["CanvasBordersFill","CanvasLineFormat"],
			plugins: ["fill","stroke","width","dash","arrow"]
		},{
			title: ["CanvasPlaceholderTextFormat", "CanvasTextFormat"],
			pluginGroups: [
				{
					groupLabel: ["PlaceholderTextFormat"],
					plugins: ["PlaceholderTextAll", "PlaceholderTextLabel", "PlaceholderTextValue"]
				},{
					groupLabel: ["ToggleLabels"],
					plugins: ["ToggleLabels"]
				},{
					groupLabel: ["FontFamily"],
					plugins: ["fontFamily"]
				},{
					groupLabel: ["FontWeight"],
					plugins: ["fontWeight"]
				},{
					groupLabel: ["ParagraphFormat"],
					plugins: ["paragraph"]
				},{
					groupLabel: ["FontSize"],
					plugins: ["fontSize"]
				},{
					groupLabel: ["Alignment"],
					plugins: ["textLeft","textCenter","textRight"]
				},{
					groupLabel: ["VerticalAlign"],
					plugins: ["verticalTop","verticalCenter","verticalBottom"]
				},{
					groupLabel: ["Autosize"],
					plugins: ["fixedWidth","fixedHeight","fixedSize"]
				},{
					groupLabel: ["Style"],
					plugins: ["strong","italic","underline", "strikethrough"]
				},{
					groupLabel: ["Color"],
					plugins: ["textColor"]
				},{
					groupLabel: ["ListType"],
					plugins: ["orderList", "unorderList"]
				},{
					groupLabel: ["Indentation"],
					plugins: ["outdent", "indent"]
				},{
					groupLabel: ["Link"],
					plugins: ["link"]
				}
			]
		},{
			title: ["PlaceholderShape"],
			plugins: ["CanvasScaleImage", "placeholderPoints", "imagePlaceholderShape", "addImageToPlaceholder", "ImageReset"]
		},{
			title:["ToggleAutospace"],
			pluginGroups: [
				{
					groupLabel:["ToggleAutospace"],
					plugins: ["ToggleAutospace"]
				}
			]
		}
	];

	/**
	 * Return category for shape type
	 * @param {string} shapeType
	 * @return {string}
	 */
	function _getShapeCategory(canvasData) {
		shapeType = canvasData.shape || "curve";

		switch (shapeType) {
			case "image":
				return shapeType;

			case "curve":
			case "line":
				return "path";

			default:
				if (canvasData.isPlaceholder) {
					return "image-placeholder";
				}
				return "other";
		}
	}

	return PluginFactory.declare([Toolbar,_Plugin], {
		pluginName: "composite",
		templateString: '<div class="csi-canvas-toolbar"><div data-dojo-type="dijit/Toolbar" data-dojo-attach-point="dijitToolbarNode"></div></div>',
		toolbarNode: {},

		/**
		 * Adds and moves plugins in to its own group container in the toolbar
		 * @param {Array} plugins
		 * @param {DomNode} container
		 */
		createPlugins: function(plugins, container) {
			_.each(plugins, function(p) {
				var plugin = this.editor.addPlugin(p, this.toolbarNode);
				var pluginDom = $(plugin.domNode).detach();
				$(container).append(pluginDom);
			}, this);
		},

		/**
		 * Update visibility of toolbars and always return true indicating this composite widget is enabled
		 * @param {Shape[]} shapes
		 * @param {Object} shapeAttrs
		 * @param {Object} textAttrs
		 * @return {boolean}
		 */
		enablePlugin: function(shapes, shapeAttrs, textAttrs) {
			const {
				domNode,
				editor,
			} = this;

			let category;
			if (shapes && shapes.length) {
				// collect types from selected shapes
				const categories = {};
				this._getShapeCategories(shapes, categories);
				category = _.keys(categories).sort().join("-");
			} else if (!editor.isSelectCanvasMode()) {
				// get type from current draw mode
				category = _getShapeCategory(editor.canvasData);
			}
			
			let css = "csi-canvas-toolbar";
			if (category) {
				if (editor.canvasApp.pageHelper.isDragTypeInstanceMode()) {
					css += " csi-canvas-toolbar-" + category;
				} else {
					css += " csi-canvas-toolbar-active csi-canvas-toolbar-" + category;
				}
			}

			domNode.className = css;
			return true;
		},

		/**
		 * Traverse shapes to compile list of shape types; groups are traversed to find their contained shapes.
		 * For the purposes of the toolbar to separate functionality, the available types are "image", "path", and "other"
		 * @param {Shape[]} shapes
		 * @param {Object} categories
		 * @param {boolean} inGroup - flag to indicate if looking inside of a group
		 */
		_getShapeCategories: function (shapes, categories, inGroup) {
			_.each(shapes, (shape) => {
				// ignore locked shapes
				if (!util.isShapeLocked(shape)) {
					if (shape.childObjects) {
						this._getShapeCategories(shape.childObjects, categories, true);
					} else if (shape.imagePlaceholder || shape.isPictureFromPlaceholder) {
						categories.image = true;
					} else if ((shape.attributeKey && shape.isTextShape && !inGroup)) {
						categories.attribute = true;
					} else if (shape.attributeKey) {
						categories.attributes = true;
					} else if (shape.isPicture) {
						categories.picture = true;
					} else if (shape.allowsArrows) {
						categories.path = true;
					} else if (shape.isTextShape) {
						categories.text = true;
					} else {
						categories.other = true;
					}
				}
			});
		},

		postCreate: function() {
			this.inherited(arguments);

			dojoExtensions.disableToolbarFocus(this.dijitToolbarNode);
			this.toolbarNode.dijitToolbarNode = this.dijitToolbarNode;
			this.toolbarNode._pluginWidgets = {};

			_.each(PLUGINS, function(val) {
				var container = html.create("div", {
					"class": 'csi-canvas-plugin-container csi-canvas-plugin-' + val.title.join(' csi-canvas-plugin-')
				}, this.dijitToolbarNode.domNode);

				var titleContainer = html.create("div", {
					"class": "csi-canvas-plugin-label"
				}, container);

				// if multiple title exists for a plugin set then add css class to control the visiblity based on selection
				_.each(val.title, function(val, i) {
					html.create("span", {
						"class": "csi-canvas-plugin-label-" + i,
						innerText: locale.resource(val)
					}, titleContainer);
				});

				if (val.pluginGroups) {
					_.each(val.pluginGroups, function(group) {
						var groupContainer = html.create("div", {
							"class": "csi-canvas-plugin-group"
						}, container);

						if (group.groupLabel) {
							html.create("span", {
								"class": `dijitInline csi-canvas-plugin-group-label csi-canvas-plugin-group-${group.groupLabel[0]}`,
								innerText: locale.resource(`CanvasGroupLabel${group.groupLabel[0]}`)
							}, groupContainer);
						}

						this.createPlugins(group.plugins, groupContainer);
					}, this);
				} else {
					this.createPlugins(val.plugins, container);
				}
			}, this);
		}
	});
});
